import { inject } from '@angular/core';
import { Router } from '@angular/router';

import { AuthStateService } from '@shared/services/auth-state.service';
import JWTTokenService from '@shared/services/jwt-token.service';
import { LoggerService } from '@shared/services/logger.service';

/**
 * AuthGuard with Role-Based Access Control (RBAC)
 *
 * Usage:
 * ```typescript
 * // Authenticated only (requires ROLE_USER by default)
 * canActivate: [AuthGuard()]
 *
 * // Specific roles required
 * canActivate: [() => AuthGuard(['ROLE_ADMIN'])]
 * canActivate: [() => AuthGuard(['ROLE_ADMIN', 'ROLE_MODERATOR'])] // OR logic
 * ```
 *
 * @param requiredRoles Optional array of roles required to access the route.
 *                      If not provided, only authentication is checked.
 *                      ROLE_USER is always required for authenticated users.
 * @returns boolean - true if access granted, false otherwise
 */
export const AuthGuard = (requiredRoles?: string[]) => {
  const authState = inject(AuthStateService);
  const jwtService = inject(JWTTokenService);
  const router = inject(Router);
  const logger = inject(LoggerService);

  // Check authentication state (httpOnly cookies)
  if (!authState.isAuthenticated()) {
    logger.security('Unauthenticated access attempt', {
      attemptedRoute: router.url,
    });

    // Clear any remaining auth data
    jwtService.clear();
    authState.clearAuthState();
    router.navigateByUrl('/login');
    return false;
  }

  // Get user info and roles
  const userInfo = authState.getUserInfo();
  const userRoles = userInfo?.roles || [];

  // CRITICAL: All authenticated users MUST have ROLE_USER
  if (!userRoles.includes('ROLE_USER')) {
    logger.security('User missing mandatory ROLE_USER', {
      userId: userInfo?.id,
      email: userInfo?.email,
      currentRoles: userRoles,
      attemptedRoute: router.url,
    });

    // Invalid user state - clear and redirect
    jwtService.clear();
    authState.clearAuthState();
    router.navigateByUrl('/login');
    return false;
  }

  // Check specific role requirements if provided
  if (requiredRoles && requiredRoles.length > 0) {
    const hasRequiredRole =
      userRoles.includes('ROLE_SUPER_ADMIN') ||
      requiredRoles.some((role) => userRoles.includes(role));

    if (!hasRequiredRole) {
      logger.security('Access denied - insufficient permissions', {
        userId: userInfo?.id,
        email: userInfo?.email,
        requiredRoles,
        userRoles,
        attemptedRoute: router.url,
      });

      // Redirect to 403 Forbidden page
      router.navigateByUrl('/403');
      return false;
    }
  }

  // Access granted
  logger.info('Route access granted', {
    userId: userInfo?.id,
    roles: userRoles,
  });

  return true;
};
